/**
 * trialActivation — in-memory signal for the Stripe trial sign-up window.
 *
 * When a user clicks "Start free trial" we set a short-lived flag before
 * redirecting to Stripe checkout. On return the flag:
 *
 *   1. Suppresses the persistent paywall gate (AppPaywallController) while
 *      the webhook hasn't landed yet.
 *   2. Drives the "Activating your trial…" UI in PlanCard.
 *   3. Keeps TrialActivationWatcher polling until the subscription resolves.
 *
 * TTL: 3 minutes. After that, normal gating resumes.
 *
 * NOTE: This is intentionally in-memory only (not localStorage). The flag
 * is set immediately before navigating away to Stripe, so it survives the
 * full-page redirect and is re-evaluated when the page reloads on return.
 * Because the page unloads during the Stripe redirect the in-memory flag
 * is gone on return — the `stripe_session_id` query param covers that gap
 * (AppPaywallController and WorkspaceHub both check it).
 */

let _activatingExpiresAt: number | null = null;

const TTL_MS = 3 * 60 * 1000; // 3 minutes

const ONBOARDING_CHECKOUT_KEY = "humblytics:onboarding:post_checkout";
const ONBOARDING_CHECKOUT_TTL_MS = 30 * 60 * 1000; // 30 minutes

/** Call this immediately before redirecting to Stripe checkout. */
export function markTrialActivating(): void {
  _activatingExpiresAt = Date.now() + TTL_MS;
}

/** Call this once the subscription has resolved to a paid/trial state. */
export function clearTrialActivating(): void {
  _activatingExpiresAt = null;
}

/**
 * Set before redirecting to Stripe checkout from the /onboarding/plan step.
 * Consumed once on return to redirect the user into the wizard instead of /dashboard.
 */
export function markOnboardingCheckout(): void {
  try {
    localStorage.setItem(
      ONBOARDING_CHECKOUT_KEY,
      String(Date.now() + ONBOARDING_CHECKOUT_TTL_MS),
    );
  } catch {
    // Ignore storage errors.
  }
}

/**
 * Returns true and clears the flag if it exists and has not expired.
 * Call this once on the Stripe return landing (WorkspaceHub / DashboardLayout).
 *
 * Checks both localStorage (set by PlanExplorerDialog in-app) and a
 * cross-origin cookie (set by the marketing site modal before Stripe redirect).
 */
export function consumeOnboardingCheckout(): boolean {
  // Check localStorage first (in-app flow)
  try {
    const raw = localStorage.getItem(ONBOARDING_CHECKOUT_KEY);
    if (raw) {
      const expiresAt = parseInt(raw, 10);
      localStorage.removeItem(ONBOARDING_CHECKOUT_KEY);
      if (Date.now() <= expiresAt) return true;
    }
  } catch {
    // ignore
  }

  // Fallback: cross-origin cookie set by the marketing site modal
  try {
    const cookieName = 'humblytics_onboarding_checkout';
    const match = document.cookie
      .split('; ')
      .find((c) => c.startsWith(cookieName + '='));
    if (match) {
      // Clear it — consume once
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.humblytics.com; SameSite=Lax`;
      return true;
    }
  } catch {
    // ignore
  }

  return false;
}

/**
 * Returns true if the activation flag is set and has not expired.
 */
export function isTrialActivatingFresh(): boolean {
  if (_activatingExpiresAt === null) return false;
  if (Date.now() > _activatingExpiresAt) {
    _activatingExpiresAt = null;
    return false;
  }
  return true;
}
